/*
 * <pewna-product-gallery> — vanilla carousel for product page gallery.
 * <pewna-product-form>    — variant selector + ATC for PDP.
 *
 * Both are progressive enhancements over Shopify's standard product object.
 * Form posts to /cart/add — Shopify handles cart insertion server-side.
 */

/*
 * <pewna-product-gallery>
 * Scroll-snap carousel with native horizontal swipe (touch + mouse drag),
 * arrow nav, dots, thumbnails, and a first-load nudge hint to make swipe
 * affordance discoverable on touch devices.
 *
 * Active slide is tracked via IntersectionObserver on the track, so arrow
 * clicks, dot clicks, thumb clicks and user swipes all stay in sync.
 * Videos auto-pause when their slide leaves the viewport.
 */
class PewnaProductGallery extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.handlePrev = this.handlePrev.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this._scrollRaf = 0;
  }

  connectedCallback() {
    this.track  = this.querySelector('[data-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.dots   = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    this.prevBtn = this.querySelector('[data-gallery-prev]');
    this.nextBtn = this.querySelector('[data-gallery-next]');
    this.counter = this.querySelector('[data-gallery-counter-current]');
    this.videos  = Array.from(this.querySelectorAll('[data-gallery-video]'));

    if (this.slides.length <= 1) {
      this.setActive(0);
      return;
    }

    this.prevBtn?.addEventListener('click', this.handlePrev);
    this.nextBtn?.addEventListener('click', this.handleNext);

    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.goTo(i));
    });

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i));
    });

    // Track active slide via scroll position — works for both arrow nav and
    // user-driven touch/mouse swipes. rAF-throttled to keep paint cheap.
    this.track?.addEventListener('scroll', this.handleScroll, { passive: true });

    // Auto play/pause videos based on which slide is centered.
    // Videos are muted+loop+autoplay so this just gates by visibility.
    if ('IntersectionObserver' in window && this.videos.length) {
      this._videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              const p = video.play();
              if (p && typeof p.catch === 'function') p.catch(() => {});
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { root: this.track, threshold: [0, 0.5, 1] }
      );
      this.videos.forEach((v) => this._videoObserver.observe(v));
    }

    // Keyboard support on the gallery frame (arrow keys move slides)
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  { this.handlePrev(); }
      if (e.key === 'ArrowRight') { this.handleNext(); }
    });

    this.setActive(0);
    this.scheduleNudgeHint();
  }

  disconnectedCallback() {
    this.prevBtn?.removeEventListener('click', this.handlePrev);
    this.nextBtn?.removeEventListener('click', this.handleNext);
    this.track?.removeEventListener('scroll', this.handleScroll);
    this._videoObserver?.disconnect();
    cancelAnimationFrame(this._scrollRaf);
  }

  handleScroll() {
    if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
    this._scrollRaf = requestAnimationFrame(() => {
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let nearest = 0;
      let nearestDist = Infinity;
      this.slides.forEach((slide, i) => {
        const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
        const dist = Math.abs(slideCenter - center);
        if (dist < nearestDist) {
          nearest = i;
          nearestDist = dist;
        }
      });
      if (nearest !== this.activeIndex) this.setActive(nearest);
    });
  }

  goTo(index) {
    if (!this.track || !this.slides.length) return;
    const total = this.slides.length;
    const next = ((index % total) + total) % total;
    const target = this.slides[next];
    if (!target) return;
    this.track.scrollTo({
      left: target.offsetLeft,
      behavior: 'smooth',
    });
    this.setActive(next);
  }

  handlePrev() { this.goTo(this.activeIndex - 1); }
  handleNext() { this.goTo(this.activeIndex + 1); }

  setActive(index) {
    this.activeIndex = index;

    this.thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    this.dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    if (this.counter) {
      this.counter.textContent = String(index + 1);
    }
  }

  /**
   * On the first scroll-into-view of the gallery, do a small horizontal nudge
   * (translate the track ~24px and back) so first-time visitors on touch
   * devices see that swiping is possible. Only fires once per page load, only
   * if the user hasn't already interacted with the track.
   */
  scheduleNudgeHint() {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (this.slides.length <= 1) return;

    let userInteracted = false;
    const markInteracted = () => { userInteracted = true; };
    this.track?.addEventListener('pointerdown', markInteracted, { once: true });
    this.track?.addEventListener('touchstart',  markInteracted, { once: true, passive: true });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();

        setTimeout(() => {
          if (userInteracted || !this.track) return;
          if (this.track.scrollLeft > 4) return; // Don't nudge if user already scrolled
          const nudge = 28;
          this.track.scrollBy({ left: nudge, behavior: 'smooth' });
          setTimeout(() => {
            if (userInteracted) return;
            this.track.scrollBy({ left: -nudge, behavior: 'smooth' });
          }, 550);
        }, 900);
      });
    }, { threshold: 0.6 });
    observer.observe(this);
  }
}

customElements.define('pewna-product-gallery', PewnaProductGallery);


class PewnaProductForm extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.handleChange = this.handleChange.bind(this);
  }

  connectedCallback() {
    const dataEl = this.querySelector('[data-product-json]');
    if (dataEl) {
      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[pewna-product-form] Invalid JSON', e);
        this.variants = [];
      }
    }

    this.form         = this.querySelector('[data-product-form]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.priceWrapper = this.querySelector('[data-price]');
    this.priceCurrent = this.querySelector('[data-price-current]');
    this.priceCompare = this.querySelector('[data-price-compare]');
    this.priceBadge   = this.querySelector('[data-price-badge]');
    this.atcButton    = this.querySelector('[data-atc-button]');
    this.atcLabel     = this.querySelector('[data-atc-label]');
    this.stockDot     = this.querySelector('[data-stock-dot]');
    this.stockLabel   = this.querySelector('[data-stock-label]');

    // Display-only compare-at fallback (set via section setting). Numeric
    // value in cents; used when admin's variant.compare_at_price is missing
    // or lower than this override. Cart/checkout always uses real prices.
    const fakeAttr = this.priceWrapper?.dataset.fakeCompare;
    this.fakeCompareCents = fakeAttr ? parseInt(fakeAttr, 10) : 0;

    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.addEventListener('change', this.handleChange);
    });

    // Initial stock state — read from currently selected size
    this.updateStockFromSelection();
  }

  updateStockFromSelection(variantAvailable) {
    if (!this.stockDot || !this.stockLabel) return;

    const sizeInput = this.querySelector('input[type="radio"][data-stock-level]:checked');
    if (!sizeInput) {
      this.stockDot.removeAttribute('data-level');
      this.stockLabel.textContent = '';
      return;
    }

    let level = sizeInput.dataset.stockLevel || 'in_stock';
    if (variantAvailable === false) {
      level = 'out';
    }

    const labels = {
      in_stock: 'W magazynie — wysyłka jutro',
      low:      'Ostatnie sztuki — zamów szybko',
      out:      'Wyprzedane — wybierz inny rozmiar',
    };

    this.stockDot.setAttribute('data-level', level);
    this.stockLabel.textContent = labels[level] || labels.in_stock;
  }

  getSelectedOptions() {
    const opts = [];
    this.querySelectorAll('[data-option-position]').forEach((group) => {
      const pos = Number(group.dataset.optionPosition);
      const checked = group.querySelector('input:checked');
      opts[pos - 1] = checked ? checked.value : null;

      // Update Polish display name + Nº number for color options
      const display = group.querySelector('[data-option-display]');
      if (display && checked) {
        display.textContent = checked.dataset.displayValue || checked.value;
      }
      const number = group.querySelector('[data-option-number]');
      if (number && checked && checked.dataset.displayNumber) {
        number.textContent = 'Nº ' + checked.dataset.displayNumber;
      }
    });
    return opts;
  }

  findVariant(opts) {
    return this.variants.find((v) => {
      if (opts[0] !== undefined && v.option1 !== opts[0]) return false;
      if (opts[1] !== undefined && v.option2 !== opts[1]) return false;
      if (opts[2] !== undefined && v.option3 !== opts[2]) return false;
      return true;
    });
  }

  formatMoney(cents) {
    if (!window.Shopify?.formatMoney) {
      const zl = (cents / 100).toFixed(2).replace('.', ',');
      return `${zl} zł`;
    }
    return Shopify.formatMoney(cents);
  }

  handleChange() {
    const opts = this.getSelectedOptions();
    const variant = this.findVariant(opts);

    // Always update stock indicator first so the dot reflects the choice
    // even when the variant lookup fails or the size is unavailable.
    this.updateStockFromSelection(variant && variant.available);

    if (!variant) {
      this.setUnavailable();
      return;
    }

    if (this.variantInput) this.variantInput.value = String(variant.id);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    if (this.priceCurrent) this.priceCurrent.textContent = this.formatMoney(variant.price);

    if (this.priceCompare) {
      // Use admin compare-at if higher than current price, else fall back to
      // the section-setting "fake" compare-at (display only).
      let compareCents = variant.compare_at_price && variant.compare_at_price > variant.price
        ? variant.compare_at_price
        : 0;
      if (!compareCents && this.fakeCompareCents > variant.price) {
        compareCents = this.fakeCompareCents;
      }

      if (compareCents && compareCents > variant.price) {
        this.priceCompare.textContent = this.formatMoney(compareCents);
        this.priceCompare.style.display = '';
        if (this.priceBadge) {
          const discount = Math.round(((compareCents - variant.price) / compareCents) * 100);
          this.priceBadge.textContent = discount + '% OFF';
          this.priceBadge.style.display = '';
        }
      } else {
        this.priceCompare.style.display = 'none';
        if (this.priceBadge) this.priceBadge.style.display = 'none';
      }
    }

    if (this.atcButton && this.atcLabel) {
      if (variant.available) {
        this.atcButton.disabled = false;
        this.atcLabel.innerHTML = 'Dodaj do koszyka — <span data-atc-price>' + this.formatMoney(variant.price) + '</span>';
      } else {
        this.atcButton.disabled = true;
        this.atcLabel.textContent = 'Wyprzedane';
      }
    }
  }

  setUnavailable() {
    if (this.atcButton) this.atcButton.disabled = true;
    if (this.atcLabel) this.atcLabel.textContent = 'Niedostępne';
  }
}

customElements.define('pewna-product-form', PewnaProductForm);
